import { useState, useMemo, useCallback, useRef } from 'react';
import { Calendar, dateFnsLocalizer, Views, type View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CalendarDays, List, LayoutGrid, Columns, Plus } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, jobService, availabilityService } from '../services/api';
import { AddTimeOffModal, RemoveTimeOffModal } from '../components/AvailabilityModals';
import { JobType, JobStatus } from '../types';
import type { JobSummaryDto, AvailabilityDto } from '../types';
import { AvailabilityKind, AVAILABILITY_KIND_LABELS } from '../types';

// ── Localizer ────────────────────────────────────────────────────────────────

const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// ── Colour palette by job type ────────────────────────────────────────────────

const TYPE_PALETTE: Record<number, { bg: string; border: string; text: string; dot: string }> = {
  [JobType.SameDayDelivery]:         { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af', dot: '#3b82f6' },
  [JobType.ScheduledDelivery]:       { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3', dot: '#6366f1' },
  [JobType.RelayDelivery]:           { bg: '#f3e8ff', border: '#a855f7', text: '#7e22ce', dot: '#a855f7' },
  [JobType.VehicleRepair]:           { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', dot: '#f59e0b' },
  [JobType.Removal]:                 { bg: '#d1fae5', border: '#10b981', text: '#065f46', dot: '#10b981' },
  [JobType.FreightHaulage]:          { bg: '#ccfbf1', border: '#14b8a6', text: '#134e4a', dot: '#14b8a6' },
  [JobType.MultiDropDelivery]:       { bg: '#cffafe', border: '#06b6d4', text: '#164e63', dot: '#06b6d4' },
  [JobType.MultiPickupDropDelivery]: { bg: '#e0f2fe', border: '#0ea5e9', text: '#075985', dot: '#0ea5e9' },
};
const DEFAULT_PALETTE = { bg: '#f3f4f6', border: '#9ca3af', text: '#374151', dot: '#9ca3af' };

const JOB_TYPE_LABELS: Record<number, string> = {
  [JobType.SameDayDelivery]:         'Same-Day',
  [JobType.ScheduledDelivery]:       'Scheduled',
  [JobType.RelayDelivery]:           'Relay',
  [JobType.VehicleRepair]:           'Repair',
  [JobType.Removal]:                 'Removal',
  [JobType.FreightHaulage]:          'Haulage',
  [JobType.MultiDropDelivery]:       'Multi Drop',
  [JobType.MultiPickupDropDelivery]: 'Multi P+D',
};

const JOB_STATUS_LABELS: Record<number, string> = {
  [JobStatus.Draft]:             'Draft',
  [JobStatus.PendingReview]:     'Pending Review',
  [JobStatus.Active]:            'Active',
  [JobStatus.Quoted]:            'Quoted',
  [JobStatus.Accepted]:          'Accepted',
  [JobStatus.ContractGenerated]: 'Contract Ready',
  [JobStatus.InProgress]:        'In Progress',
  [JobStatus.Completed]:         'Completed',
  [JobStatus.PodVerified]:       'POD Verified',
  [JobStatus.PaymentReleased]:   'Paid',
  [JobStatus.ReviewUnlocked]:    'Review Unlocked',
  [JobStatus.Cancelled]:         'Cancelled',
  [JobStatus.Rejected]:          'Rejected',
  [JobStatus.CarrierAccepted]:   'Driver Accepted',
  [JobStatus.Paused]:            'Paused',
};

const STATUS_DOT: Record<number, string> = {
  [JobStatus.InProgress]:  '#22c55e',
  [JobStatus.Paused]:      '#f59e0b',
  [JobStatus.Cancelled]:   '#ef4444',
  [JobStatus.Rejected]:    '#ef4444',
  [JobStatus.Completed]:   '#6b7280',
  [JobStatus.PodVerified]: '#6b7280',
};

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * The calendar carries two kinds of thing: scheduled jobs, and the provider's own
 * unavailability. They share a surface because that is the question being asked — "what does
 * my week look like?" — and keeping them apart would mean two calendars to cross-reference.
 */
interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: JobSummaryDto | AvailabilityResource;
}

interface AvailabilityResource {
  isAvailability: true;
  block: AvailabilityDto;
}

function isAvailabilityEvent(e: CalendarEvent): e is CalendarEvent & { resource: AvailabilityResource } {
  return (e.resource as AvailabilityResource).isAvailability === true;
}

/**
 * A block with a positive capacity override is not "closed" — it is a busy day. Shading it
 * the same as a holiday would tell the provider they are unavailable when they are not.
 */
function isFullBlock(b: AvailabilityDto): boolean {
  return (b.capacityOverride ?? 0) === 0;
}

function availabilityToEvent(b: AvailabilityDto): CalendarEvent {
  const start = new Date(b.startDate);
  const end   = new Date(b.endDate);

  if (b.isAllDay) {
    // react-big-calendar treats the end of an all-day range as exclusive, so a single-day
    // block needs the end pushed to the next day or it renders as zero-width.
    end.setDate(end.getDate() + 1);
  } else {
    start.setHours(b.startHour ?? 0, 0, 0, 0);
    end.setHours(b.endHour ?? 23, 0, 0, 0);
  }

  const label = AVAILABILITY_KIND_LABELS[b.kind] ?? 'Unavailable';
  const title = isFullBlock(b)
    ? (b.note ? `${label} — ${b.note}` : label)
    : `Reduced capacity (${b.capacityOverride})`;

  return {
    id: `availability-${b.availabilityId}`,
    title,
    start,
    end,
    allDay: b.isAllDay,
    resource: { isAvailability: true, block: b },
  };
}

type FilterMode = 'all' | 'deliveries' | 'repairs';

// ── Helpers ───────────────────────────────────────────────────────────────────

function paletteFor(job: JobSummaryDto) {
  return TYPE_PALETTE[job.jobType] ?? DEFAULT_PALETTE;
}

function jobToEvent(job: JobSummaryDto): CalendarEvent {
  const start = new Date(job.scheduledAt || job.createdAt);
  const end   = addDays(start, job.isSameDay ? 0 : 0);
  end.setHours(start.getHours() + 2, start.getMinutes());
  return { id: job.id, title: job.title, start, end, resource: job };
}

const DELIVERY_TYPES = new Set([
  JobType.SameDayDelivery, JobType.ScheduledDelivery, JobType.RelayDelivery,
  JobType.Removal, JobType.FreightHaulage, JobType.MultiDropDelivery, JobType.MultiPickupDropDelivery,
]);

function filterEvents(events: CalendarEvent[], mode: FilterMode): CalendarEvent[] {
  // Availability always survives the filter. Filtering to "repairs" is about which work you
  // want to look at, not a statement that you are suddenly free on your holiday.
  const keep = (e: CalendarEvent) => {
    if (isAvailabilityEvent(e)) return true;
    const job = e.resource as JobSummaryDto;
    if (mode === 'deliveries') return DELIVERY_TYPES.has(job.jobType);
    if (mode === 'repairs')    return job.jobType === JobType.VehicleRepair;
    return true;
  };
  return events.filter(keep);
}

// ── Custom event component ────────────────────────────────────────────────────

function CalendarEventBlock({ event }: { event: CalendarEvent }) {
  // Time off is not a job and has no job type to label it with.
  if (isAvailabilityEvent(event)) {
    const full = isFullBlock(event.resource.block);
    return (
      <div
        data-testid={`calendar-availability-${event.resource.block.availabilityId}`}
        className={`h-full rounded px-1.5 py-0.5 text-xs truncate leading-tight overflow-hidden ${
          full ? 'italic text-gray-600' : 'font-medium text-amber-800'
        }`}
      >
        {full ? '🚫 ' : '⚠️ '}{event.title}
      </div>
    );
  }

  const job     = event.resource as JobSummaryDto;
  const palette = paletteFor(job);
  return (
    <div
      data-testid={`calendar-event-${event.id}`}
      style={{ backgroundColor: palette.bg, borderLeft: `3px solid ${palette.border}`, color: palette.text }}
      className="h-full rounded px-1.5 py-0.5 text-xs font-medium truncate leading-tight overflow-hidden"
    >
      <span className="font-semibold">{JOB_TYPE_LABELS[job.jobType] ?? '?'}</span>
      {' — '}
      {event.title}
    </div>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

interface TooltipState {
  job: JobSummaryDto;
  x: number;
  y: number;
}

function JobTooltip({ tip }: { tip: TooltipState }) {
  const palette = paletteFor(tip.job);
  const statusDot = STATUS_DOT[tip.job.status];

  return (
    <div
      data-testid="calendar-job-tooltip"
      style={{
        position: 'fixed',
        left: Math.min(tip.x + 12, window.innerWidth - 320),
        top: Math.min(tip.y - 8, window.innerHeight - 260),
        zIndex: 9999,
        width: 300,
        pointerEvents: 'none',
      }}
      className="bg-white border border-gray-200 rounded-xl shadow-2xl p-4 text-sm"
    >
      {/* Type badge + status dot */}
      <div className="flex items-center justify-between mb-2">
        <span
          className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: palette.bg, color: palette.text, border: `1px solid ${palette.border}` }}
        >
          {JOB_TYPE_LABELS[tip.job.jobType] ?? 'Job'}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: statusDot ?? palette.dot }}
          />
          {JOB_STATUS_LABELS[tip.job.status] ?? 'Unknown'}
        </span>
      </div>

      {/* Title */}
      <p className="font-semibold text-gray-900 leading-snug mb-1">{tip.job.title}</p>
      <p className="text-xs text-gray-400 mb-3">Ref: {tip.job.referenceNumber}</p>

      {/* Route */}
      <div className="flex flex-col gap-1 mb-3">
        <div className="flex items-start gap-1.5">
          <span className="text-green-600 mt-0.5">●</span>
          <span className="text-gray-600 text-xs">{tip.job.pickupAddress}</span>
        </div>
        <div className="flex items-start gap-1.5">
          <span className="text-red-500 mt-0.5">●</span>
          <span className="text-gray-600 text-xs">{tip.job.deliveryAddress}</span>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-3 text-xs text-gray-500 border-t border-gray-100 pt-2">
        <span>{tip.job.distanceMiles.toFixed(1)} mi</span>
        {tip.job.scheduledAt && (
          <span>
            {format(new Date(tip.job.scheduledAt), 'dd MMM yyyy, HH:mm')}
          </span>
        )}
        <span className="ml-auto">{tip.job.quoteCount} quote{tip.job.quoteCount !== 1 ? 's' : ''}</span>
      </div>

      <p className="text-xs text-blue-600 mt-2 font-medium">Click to open job →</p>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────────────────────

function Legend({ filter }: { filter: FilterMode }) {
  const entries =
    filter === 'repairs'
      ? [[JobType.VehicleRepair, 'Vehicle Repair']] as [number, string][]
      : filter === 'deliveries'
      ? (Object.entries(JOB_TYPE_LABELS) as [string, string][])
          .map(([k, v]) => [Number(k), v] as [number, string])
          .filter(([t]) => DELIVERY_TYPES.has(t))
      : (Object.entries(JOB_TYPE_LABELS) as [string, string][])
          .map(([k, v]) => [Number(k), v] as [number, string]);

  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="calendar-legend">
      {entries.map(([type, label]) => {
        const p = TYPE_PALETTE[type] ?? DEFAULT_PALETTE;
        return (
          <span
            key={type}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: p.bg, color: p.text, border: `1px solid ${p.border}` }}
          >
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: p.dot }} />
            {label}
          </span>
        );
      })}
    </div>
  );
}

// ── View picker ───────────────────────────────────────────────────────────────

const VIEW_OPTIONS: { view: View; label: string; icon: React.ReactNode }[] = [
  { view: Views.MONTH,  label: 'Month',  icon: <LayoutGrid className="w-3.5 h-3.5" /> },
  { view: Views.WEEK,   label: 'Week',   icon: <Columns    className="w-3.5 h-3.5" /> },
  { view: Views.DAY,    label: 'Day',    icon: <CalendarDays className="w-3.5 h-3.5" /> },
  { view: Views.AGENDA, label: 'List',   icon: <List       className="w-3.5 h-3.5" /> },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function CalendarPage() {
  const navigate    = useNavigate();
  const { getToken, account } = useAppAuth();

  const [view, setView]       = useState<View>(Views.MONTH);
  const [date, setDate]       = useState(new Date());
  const [filter, setFilter]   = useState<FilterMode>('all');
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hideTimer             = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: jobs = [], isLoading, error } = useQuery<JobSummaryDto[]>({
    queryKey: ['my-jobs-calendar', account?.username],
    queryFn: async () => {
      const api = createApiClient(getToken);
      return jobService(api).getMyJobs();
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  // Availability for a generous window around the visible month, so paging back and forth
  // does not refetch on every click.
  const windowFrom = useMemo(() => {
    const d = new Date(date); d.setMonth(d.getMonth() - 2); d.setDate(1);
    return d.toISOString().slice(0, 10);
  }, [date]);
  const windowTo = useMemo(() => {
    const d = new Date(date); d.setMonth(d.getMonth() + 3); d.setDate(0);
    return d.toISOString().slice(0, 10);
  }, [date]);

  const { data: blocks = [], refetch: refetchBlocks } = useQuery<AvailabilityDto[]>({
    queryKey: ['availability', account?.username, windowFrom, windowTo],
    queryFn: async () => {
      const api = createApiClient(getToken);
      // A customer has no availability to manage; the endpoint is provider-only and 403s.
      try { return await availabilityService(api).get(windowFrom, windowTo); }
      catch { return []; }
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  const allEvents = useMemo(
    () => [...jobs.map(jobToEvent), ...blocks.map(availabilityToEvent)],
    [jobs, blocks],
  );
  const events    = useMemo(() => filterEvents(allEvents, filter), [allEvents, filter]);

  // ── Navigation ────────────────────────────────────────────────────────────

  const navigate_prev = useCallback(() => {
    setDate(d => {
      const nd = new Date(d);
      if (view === Views.MONTH) { nd.setMonth(nd.getMonth() - 1); }
      else if (view === Views.WEEK) { nd.setDate(nd.getDate() - 7); }
      else { nd.setDate(nd.getDate() - 1); }
      return nd;
    });
  }, [view]);

  const navigate_next = useCallback(() => {
    setDate(d => {
      const nd = new Date(d);
      if (view === Views.MONTH) { nd.setMonth(nd.getMonth() + 1); }
      else if (view === Views.WEEK) { nd.setDate(nd.getDate() + 7); }
      else { nd.setDate(nd.getDate() + 1); }
      return nd;
    });
  }, [view]);

  // ── Tooltip handlers ──────────────────────────────────────────────────────

  const showTooltip = useCallback((job: JobSummaryDto, x: number, y: number) => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setTooltip({ job, x, y });
  }, []);

  const hideTooltip = useCallback(() => {
    hideTimer.current = setTimeout(() => setTooltip(null), 150);
  }, []);

  // ── Calendar handlers ─────────────────────────────────────────────────────

  const [pendingDelete, setPendingDelete] = useState<AvailabilityDto | null>(null);
  const [showAddBlock, setShowAddBlock]   = useState(false);

  const onSelectEvent = useCallback((event: CalendarEvent) => {
    // An availability block has no job behind it — routing to /jobs/availability-<guid> would
    // land on a 404.
    if (isAvailabilityEvent(event)) {
      setPendingDelete(event.resource.block);
      return;
    }
    navigate(`/jobs/${event.id}`);
  }, [navigate]);

  const removeBlock = useCallback(async (id: string) => {
    const api = createApiClient(getToken);
    await availabilityService(api).remove(id);
    setPendingDelete(null);
    await refetchBlocks();
  }, [getToken, refetchBlocks]);

  const eventPropGetter = useCallback((event: CalendarEvent) => {
    if (isAvailabilityEvent(event)) {
      const full = isFullBlock(event.resource.block);
      // A hard block reads as "off" — muted, struck through with a dashed edge. A reduced
      // capacity day is amber, because the provider IS working, just less.
      return {
        style: full
          ? {
              backgroundColor: '#f3f4f6',
              border: '1px dashed #9ca3af',
              color: '#4b5563',
              fontStyle: 'italic' as const,
            }
          : {
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              color: '#92400e',
            },
      };
    }

    const p = paletteFor(event.resource as JobSummaryDto);
    return {
      style: {
        backgroundColor: p.bg,
        borderColor: p.border,
        color: p.text,
        border: `1px solid ${p.border}`,
      },
    };
  }, []);

  const components = useMemo(() => ({
    event: ({ event }: { event: CalendarEvent }) => (
      <div
        // The tooltip shows job details; a time-off block has none, and hovering one should
        // not leave a stale tooltip from the last job on screen.
        onMouseEnter={e => { if (!isAvailabilityEvent(event)) showTooltip(event.resource as JobSummaryDto, e.clientX, e.clientY); }}
        onMouseLeave={hideTooltip}
        onMouseMove={e => { if (!isAvailabilityEvent(event)) showTooltip(event.resource as JobSummaryDto, e.clientX, e.clientY); }}
        className="h-full"
      >
        <CalendarEventBlock event={event} />
      </div>
    ),
  }), [showTooltip, hideTooltip]);

  // ── Heading label ─────────────────────────────────────────────────────────

  const headingLabel = useMemo(() => {
    if (view === Views.MONTH)  return format(date, 'MMMM yyyy');
    if (view === Views.WEEK)   return `Week of ${format(startOfWeek(date, { weekStartsOn: 1 }), 'd MMM yyyy')}`;
    if (view === Views.DAY)    return format(date, 'EEEE, d MMMM yyyy');
    return format(date, 'MMMM yyyy');
  }, [view, date]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="py-6" data-testid="calendar-page">
      {/* Page header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-6 h-6 text-blue-600" />
          Work Calendar
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Your scheduled deliveries, repairs and jobs — month, week or day at a glance.
        </p>
        <Legend filter={filter} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          data-testid="calendar-add-holiday"
          onClick={() => setShowAddBlock(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold shadow-sm transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add time off
        </button>

        {/* View picker */}
        <div className="flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm" data-testid="calendar-view-picker">
          {VIEW_OPTIONS.map(({ view: v, label, icon }) => (
            <button
              key={v}
              data-testid={`calendar-view-${v}`}
              onClick={() => setView(v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                view === v
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {icon}{label}
            </button>
          ))}
        </div>

        {/* Nav arrows + Today */}
        <div className="flex items-center gap-1">
          <button
            data-testid="calendar-prev-btn"
            onClick={navigate_prev}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 shadow-sm transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            data-testid="calendar-today-btn"
            onClick={() => setDate(new Date())}
            className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 shadow-sm transition-colors"
          >
            Today
          </button>
          <button
            data-testid="calendar-next-btn"
            onClick={navigate_next}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 shadow-sm transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Current period label */}
        <span className="text-base font-semibold text-gray-800 flex-1" data-testid="calendar-heading">
          {headingLabel}
        </span>

        {/* Filter */}
        <div className="flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm" data-testid="calendar-filter-picker">
          {(['all', 'deliveries', 'repairs'] as FilterMode[]).map(m => (
            <button
              key={m}
              data-testid={`calendar-filter-${m}`}
              onClick={() => setFilter(m)}
              className={`px-3 py-2 text-xs font-medium transition-colors capitalize ${
                filter === m
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Loading / error */}
      {isLoading && (
        <div className="flex justify-center py-20 text-gray-400" data-testid="calendar-loading">
          Loading jobs…
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm" data-testid="calendar-error">
          Failed to load jobs. Please refresh.
        </div>
      )}

      {/* Calendar */}
      {!isLoading && !error && (
        <div
          className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
          data-testid="calendar-container"
          style={{ height: view === Views.MONTH ? 680 : view === Views.AGENDA ? 'auto' : 680, minHeight: 400 }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            view={view}
            date={date}
            onNavigate={setDate}
            onView={setView}
            onSelectEvent={onSelectEvent}
            eventPropGetter={eventPropGetter}
            components={components as any}
            toolbar={false}
            culture="en-GB"
            popup
            showMultiDayTimes
            step={30}
            timeslots={2}
            style={{ height: '100%' }}
          />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && events.length === 0 && (
        <div className="text-center py-12 text-gray-400" data-testid="calendar-empty">
          <CalendarDays className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-500">No jobs scheduled</p>
          <p className="text-sm mt-1">
            {filter !== 'all' ? 'Try changing the filter above.' : 'Jobs with scheduled dates will appear here.'}
          </p>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && <JobTooltip tip={tooltip} />}

      {showAddBlock && (
        <AddTimeOffModal
          getToken={getToken}
          onClose={() => setShowAddBlock(false)}
          onSaved={async () => { setShowAddBlock(false); await refetchBlocks(); }}
        />
      )}

      {pendingDelete && (
        <RemoveTimeOffModal
          block={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => removeBlock(pendingDelete.availabilityId)}
        />
      )}
    </div>
  );
}
